function detectIos(navigatorRef) {
  const ua = navigatorRef?.userAgent || '';
  const platform = navigatorRef?.platform || '';
  return /iPad|iPhone|iPod/i.test(ua)
    || (platform === 'MacIntel' && Number(navigatorRef?.maxTouchPoints || 0) > 1);
}

function detectStandalone(windowRef, navigatorRef) {
  return Boolean(
    windowRef?.matchMedia?.('(display-mode: standalone)')?.matches
      || navigatorRef?.standalone === true,
  );
}

export function createPwaManager({
  windowRef = globalThis?.window,
  navigatorRef = globalThis?.navigator,
} = {}) {
  let installPrompt = null;
  let registration = null;
  let updateAvailable = false;
  let reloadingForUpdate = false;

  const state = () => Object.freeze({
    installed: detectStandalone(windowRef, navigatorRef),
    ios: detectIos(navigatorRef),
    installPromptAvailable: Boolean(installPrompt),
    serviceWorkerSupported: Boolean(navigatorRef?.serviceWorker),
    serviceWorkerReady: Boolean(registration),
    updateAvailable,
  });

  function watchInstalling(worker) {
    if (!worker?.addEventListener) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigatorRef?.serviceWorker?.controller) {
        updateAvailable = true;
      }
    });
  }

  async function registerServiceWorker() {
    if (!navigatorRef?.serviceWorker?.register || !windowRef?.location) return null;
    const { protocol, hostname } = windowRef.location;
    const secure = protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
    if (!secure) return null;

    try {
      registration = await navigatorRef.serviceWorker.register('/service-worker.js', { scope: '/' });
      updateAvailable = Boolean(registration.waiting && navigatorRef.serviceWorker.controller);
      if (registration.installing) watchInstalling(registration.installing);
      registration.addEventListener?.('updatefound', () => watchInstalling(registration.installing));
      registration.update?.().catch?.(() => {});
      return registration;
    } catch (error) {
      console.warn('Bolt Forge service worker registration failed:', error);
      return null;
    }
  }

  function bindInstallEvents() {
    if (!windowRef?.addEventListener) return;

    windowRef.addEventListener('beforeinstallprompt', (event) => {
      // Native-first policy: do not cancel this browser event. Keeping it lets
      // Settings offer a compact fallback without suppressing the browser's own
      // address-bar/menu install experience.
      installPrompt = event;
    });

    windowRef.addEventListener('appinstalled', () => {
      installPrompt = null;
    });

    navigatorRef?.serviceWorker?.addEventListener?.('controllerchange', () => {
      if (!reloadingForUpdate) return;
      reloadingForUpdate = false;
      windowRef.location.reload();
    });
  }

  async function requestInstall() {
    if (detectStandalone(windowRef, navigatorRef)) return { outcome: 'installed' };
    if (!installPrompt?.prompt) return { outcome: 'browser-ui' };

    try {
      const promptEvent = installPrompt;
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      installPrompt = null;
      return choice || { outcome: 'dismissed' };
    } catch (error) {
      console.warn('Bolt Forge install prompt could not be opened:', error);
      return { outcome: 'browser-ui' };
    }
  }

  async function applyUpdate() {
    if (!registration?.waiting) return false;
    reloadingForUpdate = true;
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  }

  async function checkForUpdate() {
    try {
      await registration?.update?.();
      updateAvailable = Boolean(registration?.waiting && navigatorRef?.serviceWorker?.controller);
    } catch {
      // Update checks must never block gameplay.
    }
    return state();
  }

  bindInstallEvents();

  return Object.freeze({
    state,
    registerServiceWorker,
    requestInstall,
    applyUpdate,
    checkForUpdate,
  });
}
